type AnyRecord = Record<string, any>;

type ImpactNodeType =
  | 'world_state'
  | 'theme'
  | 'character'
  | 'arc_milestone'
  | 'scene'
  | 'event'
  | 'plot_device'
  | 'dialogue'
  | 'connection';

type ChangeKind = 'created' | 'updated' | 'deleted';

export interface ImpactItem {
  key: string;
  id: string;
  type: ImpactNodeType;
  name: string;
  reasons: string[];
  changeKind?: ChangeKind;
}

export interface ImpactReport {
  changed: ImpactItem[];
  affected: ImpactItem[];
  globalReasons: string[];
  uiOnlyChanges: boolean;
}

interface IndexedNode {
  key: string;
  id: string;
  type: ImpactNodeType;
  name: string;
  raw: AnyRecord;
}

const arr = (value: any): AnyRecord[] => {
  if (Array.isArray(value)) {
    return value.filter((item) => item && typeof item === 'object');
  }
  if (value && typeof value === 'object') {
    return Object.values(value).filter((item) => item && typeof item === 'object') as AnyRecord[];
  }
  return [];
};

const keyOf = (type: ImpactNodeType, id: string) => `${type}:${id}`;

const stableClone = (value: any): any => {
  if (Array.isArray(value)) {
    return value.map(stableClone);
  }
  if (!value || typeof value !== 'object') {
    return value;
  }

  const next: AnyRecord = {};
  Object.keys(value)
    .filter((key) => !['created_at', 'updated_at', 'backendId', 'ai_analysis'].includes(key))
    .sort()
    .forEach((key) => {
      next[key] = stableClone(value[key]);
    });
  return next;
};

const stableStringify = (value: any) => JSON.stringify(stableClone(value));

const summarizeName = (type: ImpactNodeType, raw: AnyRecord, index: number) => {
  if (type === 'world_state') {
    return raw.name || raw.premise || raw.location || `World ${index + 1}`;
  }
  return (
    raw.name ||
    raw.title ||
    raw.scene_name ||
    raw.event_name ||
    raw.speaker ||
    raw.device_type ||
    `${type} ${index + 1}`
  );
};

const collectNodes = (payload: AnyRecord | null | undefined) => {
  const nodes = new Map<string, IndexedNode>();
  const source = payload || {};

  const worlds = arr(source.worlds);
  const worldview =
    (source.worldview && typeof source.worldview === 'object' ? source.worldview : null) ||
    (source.world_state && typeof source.world_state === 'object' ? source.world_state : null) ||
    (source.worldState && typeof source.worldState === 'object' ? source.worldState : null) ||
    arr(source.world_states)[0] ||
    null;
  if (worldview) {
    worlds.push(worldview);
  }

  const push = (type: ImpactNodeType, items: AnyRecord[]) => {
    items.forEach((raw, index) => {
      const id = String(raw.id || `${type}_${index}`);
      const node: IndexedNode = {
        key: keyOf(type, id),
        id,
        type,
        name: String(summarizeName(type, raw, index)),
        raw,
      };
      nodes.set(node.key, node);
    });
  };

  push('world_state', worlds);
  push('theme', arr(source.themes));
  push('character', arr(source.characters));
  push('arc_milestone', arr(source.arc_milestones));
  push('scene', arr(source.scenes));
  push('event', arr(source.events));
  push('plot_device', arr(source.plot_devices));
  push('dialogue', arr(source.dialogues));
  push(
    'connection',
    arr(source.connections).map((item) => ({
      id: item.id,
      source_id: item.source_id || item.sourceId,
      target_id: item.target_id || item.targetId,
      relationship_type: item.relationship_type || item.relationshipType,
      semantic_family: item.semantic_family || item.semanticFamily,
      description: item.description || '',
      strength: item.strength || 1,
    }))
  );

  return nodes;
};

const addEdge = (
  adjacency: Map<string, Array<{ to: string; reason: string }>>,
  from: string,
  to: string,
  reason: string
) => {
  if (!from || !to || from === to) {
    return;
  }
  const addOne = (source: string, target: string) => {
    const list = adjacency.get(source) || [];
    if (!list.some((item) => item.to === target && item.reason === reason)) {
      list.push({ to: target, reason });
      adjacency.set(source, list);
    }
  };
  addOne(from, to);
  addOne(to, from);
};

const buildAdjacency = (payloads: Array<AnyRecord | null | undefined>, nodes: Map<string, IndexedNode>) => {
  const adjacency = new Map<string, Array<{ to: string; reason: string }>>();
  const characterNameIndex = new Map<string, string[]>();
  Array.from(nodes.values())
    .filter((node) => node.type === 'character')
    .forEach((node) => {
      const label = String(node.name || '').trim().toLowerCase();
      if (!label) {
        return;
      }
      const list = characterNameIndex.get(label) || [];
      list.push(node.key);
      characterNameIndex.set(label, list);
    });

  const maybeNodeKey = (type: ImpactNodeType, id: any) => {
    const nextId = String(id || '').trim();
    if (!nextId) {
      return '';
    }
    const nextKey = keyOf(type, nextId);
    return nodes.has(nextKey) ? nextKey : '';
  };

  payloads.forEach((payload) => {
    const source = payload || {};

    arr(source.scenes).forEach((scene, index) => {
      const sceneId = String(scene.id || `scene_${index}`);
      const sceneKey = maybeNodeKey('scene', sceneId);
      if (!sceneKey) {
        return;
      }

      ((scene.characters_present || scene.charactersPresent) || []).forEach((characterId: any) => {
        const characterKey = maybeNodeKey('character', characterId);
        if (characterKey) {
          addEdge(adjacency, sceneKey, characterKey, 'scene.characters_present');
        }
      });

      (scene.events || []).forEach((eventId: any) => {
        const eventKey = maybeNodeKey('event', eventId);
        if (eventKey) {
          addEdge(adjacency, sceneKey, eventKey, 'scene.events');
        }
      });

      ((scene.conflict_roles || scene.conflictRoles) || []).forEach((characterId: any) => {
        const characterKey = maybeNodeKey('character', characterId);
        if (characterKey) {
          addEdge(adjacency, sceneKey, characterKey, 'scene.conflict_roles');
        }
      });

      ((scene.conflict_pairs || scene.conflictPairs) || []).forEach((pair: any) => {
        const sourceKey = maybeNodeKey('character', pair?.source_id);
        const targetKey = maybeNodeKey('character', pair?.target_id);
        if (sourceKey) {
          addEdge(adjacency, sceneKey, sourceKey, 'scene.conflict_pairs');
        }
        if (targetKey) {
          addEdge(adjacency, sceneKey, targetKey, 'scene.conflict_pairs');
        }
      });
    });

    arr(source.events).forEach((event, index) => {
      const eventId = String(event.id || `event_${index}`);
      const eventKey = maybeNodeKey('event', eventId);
      if (!eventKey) {
        return;
      }

      const sceneKey = maybeNodeKey('scene', event.scene_id || event.sceneId);
      if (sceneKey) {
        addEdge(adjacency, eventKey, sceneKey, 'event.scene_id');
      }

      ((event.characters_involved || event.charactersInvolved) || []).forEach((characterId: any) => {
        const characterKey = maybeNodeKey('character', characterId);
        if (characterKey) {
          addEdge(adjacency, eventKey, characterKey, 'event.characters_involved');
        }
      });

      (event.consequences || []).forEach((eventIdRef: any) => {
        const targetKey = maybeNodeKey('event', eventIdRef);
        if (targetKey) {
          addEdge(adjacency, eventKey, targetKey, 'event.consequences');
        }
      });

      (event.foreshadowing || []).forEach((eventIdRef: any) => {
        const targetKey = maybeNodeKey('event', eventIdRef);
        if (targetKey) {
          addEdge(adjacency, eventKey, targetKey, 'event.foreshadowing');
        }
      });
    });

    arr(source.dialogues).forEach((dialogue, index) => {
      const dialogueId = String(dialogue.id || `dialogue_${index}`);
      const dialogueKey = maybeNodeKey('dialogue', dialogueId);
      if (!dialogueKey) {
        return;
      }

      const sceneKey = maybeNodeKey('scene', dialogue.scene_id || dialogue.sceneId);
      if (sceneKey) {
        addEdge(adjacency, dialogueKey, sceneKey, 'dialogue.scene_id');
      }

      const speaker = String(dialogue.speaker || '').trim();
      if (speaker) {
        const directCharacterKey = maybeNodeKey('character', speaker);
        if (directCharacterKey) {
          addEdge(adjacency, dialogueKey, directCharacterKey, 'dialogue.speaker');
        } else {
          const matches = characterNameIndex.get(speaker.toLowerCase()) || [];
          if (matches.length === 1) {
            addEdge(adjacency, dialogueKey, matches[0], 'dialogue.speaker_name');
          }
        }
      }
    });

    arr(source.arc_milestones).forEach((milestone, index) => {
      const milestoneId = String(milestone.id || `arc_milestone_${index}`);
      const milestoneKey = maybeNodeKey('arc_milestone', milestoneId);
      if (!milestoneKey) {
        return;
      }
      const characterKey = maybeNodeKey('character', milestone.character_id || milestone.characterId);
      if (characterKey) {
        addEdge(adjacency, milestoneKey, characterKey, 'arc.character_id');
      }
      const eventKey = maybeNodeKey('event', milestone.attached_event_id || milestone.attachedEventId);
      if (eventKey) {
        addEdge(adjacency, milestoneKey, eventKey, 'arc.attached_event_id');
      }
    });

    arr(source.themes).forEach((theme, index) => {
      const themeId = String(theme.id || `theme_${index}`);
      const themeKey = maybeNodeKey('theme', themeId);
      if (!themeKey) {
        return;
      }

      ((theme.related_characters || theme.relatedCharacters) || []).forEach((characterId: any) => {
        const characterKey = maybeNodeKey('character', characterId);
        if (characterKey) {
          addEdge(adjacency, themeKey, characterKey, 'theme.related_characters');
        }
      });

      ((theme.related_events || theme.relatedEvents) || []).forEach((eventId: any) => {
        const eventKey = maybeNodeKey('event', eventId);
        if (eventKey) {
          addEdge(adjacency, themeKey, eventKey, 'theme.related_events');
        }
      });
    });

    arr(source.plot_devices).forEach((device, index) => {
      const deviceId = String(device.id || `plot_device_${index}`);
      const deviceKey = maybeNodeKey('plot_device', deviceId);
      if (!deviceKey) {
        return;
      }

      const attachedSceneKey = maybeNodeKey('scene', device.attached_scene_id || device.attachedSceneId);
      if (attachedSceneKey) {
        addEdge(adjacency, deviceKey, attachedSceneKey, 'plot_device.attached_scene_id');
      }

      const attachedEventKey = maybeNodeKey('event', device.attached_event_id || device.attachedEventId);
      if (attachedEventKey) {
        addEdge(adjacency, deviceKey, attachedEventKey, 'plot_device.attached_event_id');
      }

      (device.setup_nodes || []).forEach((node: any) => {
        const sceneKey = maybeNodeKey('scene', node?.scene_id || node?.sceneId);
        if (sceneKey) {
          addEdge(adjacency, deviceKey, sceneKey, 'plot_device.setup_nodes.scene_id');
        }
        const eventKey = maybeNodeKey('event', node?.event_id || node?.eventId);
        if (eventKey) {
          addEdge(adjacency, deviceKey, eventKey, 'plot_device.setup_nodes.event_id');
        }
      });

      (device.payoff_nodes || []).forEach((node: any) => {
        const sceneKey = maybeNodeKey('scene', node?.scene_id || node?.sceneId);
        if (sceneKey) {
          addEdge(adjacency, deviceKey, sceneKey, 'plot_device.payoff_nodes.scene_id');
        }
        const eventKey = maybeNodeKey('event', node?.event_id || node?.eventId);
        if (eventKey) {
          addEdge(adjacency, deviceKey, eventKey, 'plot_device.payoff_nodes.event_id');
        }
      });

      const links = device.links || {};
      (links.characters || []).forEach((characterId: any) => {
        const characterKey = maybeNodeKey('character', characterId);
        if (characterKey) {
          addEdge(adjacency, deviceKey, characterKey, 'plot_device.links.characters');
        }
      });
      (links.scenes || []).forEach((sceneId: any) => {
        const sceneKey = maybeNodeKey('scene', sceneId);
        if (sceneKey) {
          addEdge(adjacency, deviceKey, sceneKey, 'plot_device.links.scenes');
        }
      });
      (links.relationships || []).forEach((connectionId: any) => {
        const connectionKey = maybeNodeKey('connection', connectionId);
        if (connectionKey) {
          addEdge(adjacency, deviceKey, connectionKey, 'plot_device.links.relationships');
        }
      });

      const beats = Array.isArray(links.beats) ? links.beats.map((item: any) => String(item)) : [];
      if (beats.length > 0) {
        Array.from(nodes.values())
          .filter((node) => node.type === 'event' && beats.includes(String(node.raw.beat || '')))
          .forEach((node) => addEdge(adjacency, deviceKey, node.key, 'plot_device.links.beats'));
      }

      if (Array.isArray(links.world_rules) && links.world_rules.length > 0) {
        Array.from(nodes.values())
          .filter((node) => node.type === 'world_state')
          .forEach((node) => addEdge(adjacency, deviceKey, node.key, 'plot_device.links.world_rules'));
      }
    });

    arr(source.connections).forEach((connection, index) => {
      const connectionId = String(connection.id || `connection_${index}`);
      const connectionKey = maybeNodeKey('connection', connectionId);
      if (!connectionKey) {
        return;
      }
      const sourceId = connection.source_id || connection.sourceId;
      const targetId = connection.target_id || connection.targetId;

      const candidateTypes: ImpactNodeType[] = [
        'character',
        'scene',
        'event',
        'plot_device',
        'theme',
        'dialogue',
        'arc_milestone',
        'world_state',
      ];
      candidateTypes.forEach((type) => {
        const sourceKey = maybeNodeKey(type, sourceId);
        if (sourceKey) {
          addEdge(adjacency, connectionKey, sourceKey, 'connection.source_id');
        }
        const targetKey = maybeNodeKey(type, targetId);
        if (targetKey) {
          addEdge(adjacency, connectionKey, targetKey, 'connection.target_id');
        }
      });
    });
  });

  return adjacency;
};

const diffEntities = (baseline: Map<string, IndexedNode>, current: Map<string, IndexedNode>) => {
  const changed = new Map<string, ImpactItem>();
  const keys = new Set<string>([...Array.from(baseline.keys()), ...Array.from(current.keys())]);

  keys.forEach((key) => {
    const baselineNode = baseline.get(key);
    const currentNode = current.get(key);
    if (!baselineNode && currentNode) {
      changed.set(key, {
        key,
        id: currentNode.id,
        type: currentNode.type,
        name: currentNode.name,
        reasons: ['created'],
        changeKind: 'created',
      });
      return;
    }

    if (baselineNode && !currentNode) {
      changed.set(key, {
        key,
        id: baselineNode.id,
        type: baselineNode.type,
        name: baselineNode.name,
        reasons: ['deleted'],
        changeKind: 'deleted',
      });
      return;
    }

    if (baselineNode && currentNode) {
      const before = stableStringify(baselineNode.raw);
      const after = stableStringify(currentNode.raw);
      if (before !== after) {
        changed.set(key, {
          key,
          id: currentNode.id,
          type: currentNode.type,
          name: currentNode.name,
          reasons: ['updated'],
          changeKind: 'updated',
        });
      }
    }
  });

  return changed;
};

const addGlobalReason = (reasons: Set<string>, value: string, condition: boolean) => {
  if (condition) {
    reasons.add(value);
  }
};

export const analyzeWorkspaceImpact = (
  baselinePayload: AnyRecord | null | undefined,
  currentPayload: AnyRecord | null | undefined
): ImpactReport => {
  const baselineNodes = collectNodes(baselinePayload);
  const currentNodes = collectNodes(currentPayload);
  const allNodes = new Map<string, IndexedNode>([
    ...Array.from(baselineNodes.entries()),
    ...Array.from(currentNodes.entries()),
  ]);
  const changed = diffEntities(baselineNodes, currentNodes);
  const adjacency = buildAdjacency([baselinePayload, currentPayload], allNodes);

  const baselineMeta = baselinePayload?.workspace_meta || {};
  const currentMeta = currentPayload?.workspace_meta || {};

  const globalReasons = new Set<string>();
  addGlobalReason(
    globalReasons,
    'worldview',
    stableStringify(baselinePayload?.worldview || {}) !== stableStringify(currentPayload?.worldview || {}) ||
      String(baselineMeta?.worldview_text || '') !== String(currentMeta?.worldview_text || '')
  );
  addGlobalReason(
    globalReasons,
    'themes',
    String(baselineMeta?.themes_text || '') !== String(currentMeta?.themes_text || '') ||
      Array.from(changed.values()).some((item) => item.type === 'theme')
  );

  const reasonMap = new Map<string, Set<string>>();
  const queue: string[] = [];
  changed.forEach((item, key) => {
    queue.push(key);
    reasonMap.set(key, new Set(item.reasons));
    if (item.type === 'world_state') {
      globalReasons.add('worldview');
    }
  });

  const visited = new Set(queue);
  while (queue.length > 0) {
    const currentKey = queue.shift()!;
    (adjacency.get(currentKey) || []).forEach((edge) => {
      const reasons = reasonMap.get(edge.to) || new Set<string>();
      const label = `linked:${edge.reason}`;
      reasons.add(label);
      reasonMap.set(edge.to, reasons);
      if (!visited.has(edge.to)) {
        visited.add(edge.to);
        queue.push(edge.to);
      }
    });
  }

  if (globalReasons.size > 0) {
    Array.from(currentNodes.values()).forEach((node) => {
      if (!changed.has(node.key)) {
        const reasons = reasonMap.get(node.key) || new Set<string>();
        Array.from(globalReasons).forEach((reason) => reasons.add(`global:${reason}`));
        reasonMap.set(node.key, reasons);
      }
    });
  }

  const affected = Array.from(reasonMap.entries())
    .filter(([key]) => !changed.has(key))
    .map(([key, reasons]) => {
      const node = currentNodes.get(key) || allNodes.get(key);
      if (!node) {
        return null;
      }
      return {
        key,
        id: node.id,
        type: node.type,
        name: node.name,
        reasons: Array.from(reasons),
      } as ImpactItem;
    })
    .filter(Boolean) as ImpactItem[];

  const changedItems = Array.from(changed.values());
  const layoutChanged =
    stableStringify(baselineMeta?.canvas_layout || {}) !== stableStringify(currentMeta?.canvas_layout || {});

  return {
    changed: changedItems,
    affected,
    globalReasons: Array.from(globalReasons),
    uiOnlyChanges: changedItems.length === 0 && affected.length === 0 && layoutChanged,
  };
};
